const BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3333";

const CHAVE_ACCESS = "ag5.access";
const CHAVE_REFRESH = "ag5.refresh";

export const tokens = {
  get access() {
    return sessionStorage.getItem(CHAVE_ACCESS);
  },
  get refresh() {
    return localStorage.getItem(CHAVE_REFRESH);
  },
  salvar(access: string, refresh: string) {
    sessionStorage.setItem(CHAVE_ACCESS, access);
    localStorage.setItem(CHAVE_REFRESH, refresh);
  },
  limpar() {
    sessionStorage.removeItem(CHAVE_ACCESS);
    localStorage.removeItem(CHAVE_REFRESH);
  },
};

/**
 * "Dados inválidos" sozinho não diz nada a quem está usando o sistema.
 * Quando a API manda o detalhe por campo, junta tudo numa frase legível.
 */
function mensagemDeErro(e: { message?: string; details?: unknown }): string {
  const base = e.message ?? "Erro inesperado";
  const d = e.details;
  if (!d || typeof d !== "object") return base;
  const campos = Object.entries(d as Record<string, unknown>)
    .map(([campo, msgs]) => `${campo}: ${Array.isArray(msgs) ? msgs.join(", ") : String(msgs)}`)
    .filter(Boolean);
  return campos.length ? `${base} — ${campos.join(" · ")}` : base;
}

export class ErroApi extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

async function bruto(caminho: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${BASE}${caminho}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
      ...init.headers,
    },
  });
}

let renovando: Promise<boolean> | null = null;

/** Tenta renovar o access token uma única vez, mesmo com várias chamadas simultâneas. */
async function renovar(): Promise<boolean> {
  if (!tokens.refresh) return false;
  renovando ??= (async () => {
    try {
      const r = await fetch(`${BASE}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: tokens.refresh }),
      });
      if (!r.ok) return false;
      const d = (await r.json()) as { accessToken: string; refreshToken: string };
      tokens.salvar(d.accessToken, d.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (renovando = null), 0);
    }
  })();
  return renovando;
}

export async function api<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  let resp = await bruto(caminho, init);

  // `/api/publico/` nunca exige login — mandar um visitante do site para a
  // tela de login por causa de um token velho no navegador seria absurdo
  const podeRenovar = !caminho.startsWith("/api/auth/") && !caminho.startsWith("/api/publico/");
  if (resp.status === 401 && podeRenovar) {
    if (await renovar()) resp = await bruto(caminho, init);
    else {
      tokens.limpar();
      window.location.href = "/login";
      throw new ErroApi(401, "NAO_AUTENTICADO", "Sessão expirada");
    }
  }

  if (resp.status === 204) return undefined as T;

  const corpo = await resp.json().catch(() => null);
  if (!resp.ok) {
    const e = corpo?.error ?? {};
    throw new ErroApi(resp.status, e.code ?? "ERRO", mensagemDeErro(e), e.details);
  }
  return corpo as T;
}

export const get = <T>(c: string) => api<T>(c);
export const post = <T>(c: string, body?: unknown) =>
  api<T>(c, { method: "POST", body: body ? JSON.stringify(body) : undefined });
export const patch = <T>(c: string, body: unknown) =>
  api<T>(c, { method: "PATCH", body: JSON.stringify(body) });
export const del = (c: string) => api<void>(c, { method: "DELETE" });
export const apagar = (c: string) => del(`${c}${c.includes("?") ? "&" : "?"}definitivo=true`);
